import { useParams, Link } from "react-router-dom";
import { Star, Phone, MapPin, Clock } from "lucide-react";
import SiteSmithBanner from "@/components/SiteSmithBanner";
import { SITES } from "@/lib/exampleSiteData";

const Stars = ({ count = 5, size = 14, color = "#F59E0B" }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} size={size} fill={color} color={color} strokeWidth={0} />
    ))}
  </div>
);

const Avatar = ({ id }) => (
  <img src={`https://i.pravatar.cc/100?img=${id}`} alt="" className="w-12 h-12 rounded-full object-cover" />
);

/* ───── 1. NAV ───── White bg, dark text */
function ExNav({ site }) {
  const c = site.colors;
  return (
    <nav data-testid="ex-nav" className="px-6 sm:px-10 py-4 flex items-center justify-between bg-white border-b border-gray-100">
      <span className="text-lg font-bold" style={{ color: c.primary, fontFamily: site.headingFont }}>
        {site.name}
      </span>
      <div className="hidden md:flex items-center gap-6">
        {site.navItems.map((n) => (
          <a
            key={n}
            href={`#${n.toLowerCase().replace(/\s/g, "-")}`}
            className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            {n}
          </a>
        ))}
      </div>
      <a
        href={`tel:${site.phone}`}
        className="text-sm font-semibold px-4 py-2 rounded-full text-white"
        style={{ background: c.primary }}
      >
        {site.ctaText || "Contact Us"}
      </a>
    </nav>
  );
}

/* ───── 2. HERO ───── Dark overlay on image, white text */
function ExHero({ site }) {
  const c = site.colors;
  return (
    <section
      data-testid="ex-hero"
      className="relative flex items-center justify-center text-center px-6 py-28 sm:py-36"
      style={{ minHeight: "75vh" }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${site.heroImage})` }}
      />
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(180deg, rgba(0,0,0,0.5) 0%, ${c.primary}dd 100%)`,
        }}
      />
      <div className="relative z-10 max-w-2xl">
        {site.badge && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-5 bg-white/10 backdrop-blur-sm border border-white/20">
            <span className="text-xs font-semibold text-white/90 tracking-wide">
              {site.badge}
            </span>
          </div>
        )}
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4"
          style={{ fontFamily: site.headingFont }}
        >
          {site.heroTitle}
        </h1>
        <p className="text-white/80 text-base sm:text-lg mb-8 max-w-xl mx-auto">
          {site.heroSubtitle}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a
            href="#contact"
            className="px-7 py-3 rounded-full text-white font-semibold text-sm shadow-lg transition-transform hover:scale-105"
            style={{ background: c.accent || c.primary }}
          >
            {site.ctaText || "Learn More"}
          </a>
          {site.secondaryCta && (
            <a
              href="#about"
              className="px-7 py-3 rounded-full border border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition-colors"
            >
              {site.secondaryCta}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

/* ───── 3. SERVICES ───── White bg, dark text */
function ExServices({ site }) {
  const c = site.colors;
  const layout = site.servicesLayout || "cards";

  return (
    <section id="services" data-testid="ex-services" className="py-20 px-6 sm:px-10 bg-white">
      <div className="max-w-6xl mx-auto">
        <h2
          className="text-3xl font-bold mb-2 text-gray-900"
          style={{ fontFamily: site.headingFont }}
        >
          {site.servicesTitle || "Our Services"}
        </h2>
        <div className="w-12 h-1 rounded-full mb-10" style={{ background: c.primary }} />

        {layout === "priceList" && (
          <div className="max-w-xl mx-auto">
            {site.services.map((s, i) => (
              <div
                key={i}
                className="flex items-baseline justify-between py-3 border-b border-gray-100"
              >
                <span className="font-medium text-gray-900">{s.name}</span>
                <span className="flex-1 mx-3 border-b border-dotted border-gray-200" />
                <span className="font-semibold" style={{ color: c.primary }}>
                  {s.price}
                </span>
              </div>
            ))}
          </div>
        )}

        {layout === "cards" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {site.services.map((s, i) => (
              <div
                key={i}
                className="rounded-lg border border-gray-100 p-6 hover:shadow-md transition-shadow"
              >
                <h3 className="font-bold text-lg mb-1 text-gray-900">{s.name}</h3>
                {s.desc && <p className="text-sm text-gray-500 mb-3">{s.desc}</p>}
                {s.items && (
                  <ul className="space-y-1.5">
                    {s.items.map((item, j) => (
                      <li key={j} className="text-sm flex justify-between text-gray-600">
                        <span>{item.name}</span>
                        {item.price && (
                          <span className="font-medium" style={{ color: c.primary }}>
                            {item.price}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {layout === "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {site.services.map((s, i) => (
              <div key={i} className="p-5 rounded-lg border border-gray-100 bg-gray-50/50">
                <h3 className="font-semibold text-gray-900">{s.name}</h3>
                <p className="text-sm mt-1 text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        )}

        {layout === "pills" && (
          <div className="flex flex-wrap gap-3">
            {site.services.map((s, i) => (
              <span
                key={i}
                className="px-5 py-2.5 rounded-full font-medium text-sm border border-gray-200 text-gray-700 bg-gray-50"
              >
                {s.name}
              </span>
            ))}
          </div>
        )}

        {layout === "menu" && (
          <div className="max-w-2xl mx-auto space-y-8">
            {site.services.map((cat, i) => (
              <div key={i}>
                <h3
                  className="font-bold text-lg tracking-widest uppercase mb-2"
                  style={{ color: c.primary, fontFamily: site.headingFont }}
                >
                  {cat.category}
                </h3>
                <div className="h-px bg-gray-100 mb-3" />
                <p className="text-gray-600 leading-relaxed">{cat.items}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ───── 4. ABOUT ───── Slate-50 bg, dark text, image + text */
function ExAbout({ site }) {
  const c = site.colors;
  return (
    <section id="about" data-testid="ex-about" className="py-20 px-6 sm:px-10 bg-slate-50">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div
          className="h-72 md:h-96 rounded-xl bg-cover bg-center shadow-sm"
          style={{ backgroundImage: `url(${site.aboutImage})` }}
        />
        <div>
          <h2
            className="text-3xl font-bold mb-2 text-gray-900"
            style={{ fontFamily: site.headingFont }}
          >
            {site.aboutTitle}
          </h2>
          <div className="w-12 h-1 rounded-full mb-6" style={{ background: c.primary }} />
          <p className="leading-relaxed text-gray-600 mb-4">{site.aboutText}</p>
          {site.aboutText2 && (
            <p className="leading-relaxed text-gray-500">{site.aboutText2}</p>
          )}
        </div>
      </div>
    </section>
  );
}

/* ───── 5. TESTIMONIALS ───── White bg, dark text */
function ExTestimonials({ site }) {
  const c = site.colors;
  return (
    <section data-testid="ex-testimonials" className="py-20 px-6 sm:px-10 bg-white">
      <div className="max-w-6xl mx-auto">
        <h2
          className="text-3xl font-bold mb-2 text-gray-900"
          style={{ fontFamily: site.headingFont }}
        >
          {site.testimonialsTitle || "What Our Customers Say"}
        </h2>
        <div className="w-12 h-1 rounded-full mb-10" style={{ background: c.primary }} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {site.testimonials.map((t, i) => (
            <div key={i} className="p-6 rounded-xl border border-gray-100 bg-gray-50/30">
              <Stars size={14} color={c.primary} />
              <p
                className="mt-3 text-sm leading-relaxed text-gray-600"
                style={{ fontStyle: "italic" }}
              >
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                <Avatar id={t.avatarId} />
                <div>
                  <p className="font-semibold text-sm text-gray-900">{t.name}</p>
                  {t.location && (
                    <p className="text-xs text-gray-400">{t.location}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── 6. CONTACT / HOURS ───── Slate-50 bg, dark text */
function ExContact({ site }) {
  const c = site.colors;
  return (
    <section id="contact" data-testid="ex-contact" className="py-20 px-6 sm:px-10 bg-slate-50">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
        <div>
          <h2
            className="text-3xl font-bold mb-2 text-gray-900"
            style={{ fontFamily: site.headingFont }}
          >
            Get in Touch
          </h2>
          <div className="w-12 h-1 rounded-full mb-6" style={{ background: c.primary }} />
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${c.primary}15` }}
              >
                <Phone size={18} style={{ color: c.primary }} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{site.phone}</p>
                <p className="text-sm text-gray-400">Call or text anytime</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${c.primary}15` }}
              >
                <MapPin size={18} style={{ color: c.primary }} />
              </div>
              <p className="font-semibold text-gray-900">{site.address}</p>
            </div>
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${c.primary}15` }}
              >
                <Clock size={18} style={{ color: c.primary }} />
              </div>
              <div>
                {site.hours.map((h, i) => (
                  <p key={i} className="text-sm text-gray-600">
                    {h}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-xl p-6 border border-gray-100 bg-white shadow-sm">
          <h3 className="font-bold text-lg mb-4 text-gray-900">
            {site.contactFormTitle || "Send us a message"}
          </h3>
          <div className="space-y-3">
            <input
              placeholder="Your Name"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
            <input
              placeholder="Phone or Email"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
            <textarea
              placeholder="How can we help?"
              rows={3}
              className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white placeholder-gray-400 resize-none focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
            <button
              className="w-full py-3 rounded-lg text-white font-semibold transition-transform hover:scale-[1.01]"
              style={{ background: c.primary }}
            >
              Send Message
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── 7. FOOTER ───── Dark bg (primary), white text */
function ExFooter({ site }) {
  const c = site.colors;
  return (
    <footer data-testid="ex-footer" className="py-10 px-6" style={{ background: c.primary }}>
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <span className="font-bold text-white" style={{ fontFamily: site.headingFont }}>
          {site.name}
        </span>
        <p className="text-sm text-white/50">
          &copy; 2026 {site.name}. All rights reserved.
        </p>
        <p className="text-xs text-white/30">
          Website by{" "}
          <Link to="/" className="underline hover:text-white/60">
            SiteSmith
          </Link>
        </p>
      </div>
    </footer>
  );
}

/* ===== MAIN RENDERER ===== */
export default function ExampleSite() {
  const { slug } = useParams();
  const site = SITES[slug];

  if (!site) {
    return (
      <div className="min-h-screen bg-[#0A0F1F] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white font-bold mb-4">Example not found</h1>
          <Link to="/" className="text-orange-500 hover:underline">
            Back to SiteSmith
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`example-site-${slug}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <SiteSmithBanner businessName={site.name} />
      <ExNav site={site} />
      <ExHero site={site} />
      <ExServices site={site} />
      <ExAbout site={site} />
      <ExTestimonials site={site} />
      <ExContact site={site} />
      <ExFooter site={site} />
    </div>
  );
}
